<script setup>
// SiteUp (M3/M4): selo de apagada/editada + original INLINE.
// Superfície e estado visual ficam no Base; aqui só o texto e o toggle.
// Cores por herança + opacidade (bolha de saída é azul/branco: slate
// fixo some, como no #438 da enquete).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatClockTime } from 'dashboard/helper/messageLifecycle';

const props = defineProps({
  revokedAt: { type: String, default: '' },
  revokedBy: { type: String, default: '' },
  edited: { type: Boolean, default: false },
  editedAt: { type: String, default: '' },
  originalContent: { type: String, default: '' },
  // createdAt do Chatwoot (segundos) — rótulo "Original, HH:MM".
  createdAt: { type: [Number, String], default: null },
});

const { t } = useI18n();
const showingOriginal = ref(false);

const isRevoked = computed(() => Boolean(props.revokedAt));
const hasOriginal = computed(
  () => props.edited && Boolean(props.originalContent)
);

const revokedLabel = computed(() => {
  const time = formatClockTime(props.revokedAt);
  const key =
    props.revokedBy === 'agent'
      ? 'CONVERSATION.REVOKED_BY_AGENT_AT'
      : 'CONVERSATION.REVOKED_BY_CONTACT_AT';
  return t(key, { time });
});

const editedTimeLabel = computed(() => formatClockTime(props.editedAt));

const originalHeading = computed(() =>
  t('CONVERSATION.ORIGINAL_AT', {
    time: formatClockTime(props.createdAt),
  })
);

const toggleOriginal = () => {
  if (!hasOriginal.value) return;
  showingOriginal.value = !showingOriginal.value;
};
</script>

<template>
  <div
    v-if="isRevoked || edited"
    class="mt-2 flex flex-col gap-1.5"
    data-testid="message-lifecycle"
  >
    <div
      v-if="isRevoked"
      data-testid="message-revoked-label"
      class="inline-flex items-center gap-1 text-[10px] font-semibold opacity-80"
    >
      <span class="i-ph-trash size-3 shrink-0" />
      <span>{{ revokedLabel }}</span>
    </div>

    <div
      v-else-if="edited"
      data-testid="message-edited-label"
      class="inline-flex flex-wrap items-center gap-1 text-[10px] font-semibold opacity-80"
    >
      <span class="i-ph-pencil-simple size-3 shrink-0" />
      <span>
        {{ t('CONVERSATION.EDITED_AT_SHORT', { time: editedTimeLabel }) }}
      </span>
      <template v-if="hasOriginal">
        <span aria-hidden="true">·</span>
        <button
          type="button"
          data-testid="message-original-toggle"
          class="min-h-7 underline-offset-2 hover:underline focus:underline"
          :aria-expanded="showingOriginal"
          aria-controls="message-original-content"
          @click.stop="toggleOriginal"
        >
          {{
            showingOriginal
              ? t('CONVERSATION.LIFECYCLE_HIDE_ORIGINAL')
              : t('CONVERSATION.LIFECYCLE_VIEW_ORIGINAL')
          }}
        </button>
      </template>
    </div>

    <!-- Abaixo do selo de propósito: o botão não pula quando o original abre
         (celular). Sem tooltip — o bloco inteiro precisa caber no toque. -->
    <div
      v-if="showingOriginal && hasOriginal"
      id="message-original-content"
      data-testid="message-original-content"
      class="rounded-xl bg-current/10 px-2.5 py-2"
      @click.stop
    >
      <div class="text-[10px] font-semibold opacity-70">
        {{ originalHeading }}
      </div>
      <p class="mt-1 whitespace-pre-wrap text-sm opacity-80">
        {{ originalContent }}
      </p>
    </div>
  </div>
</template>
